import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, AlertTitle, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Checkbox, Collapse, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SftGlobalData from '../../library/SftGlobalData';
import { webshopErrorText } from './_webshopNotice';

// The shop URL without the mini-api suffix auth stores on display_name.
export const shopBaseUrl = (w) => String((w && (w.display_name || w.url)) || '')
    .trim().replace(/\/server\/v1\/mini-api\/?$/i, '').replace(/\/+$/, '');
const hostOf = (u) => { try { return new URL(u).host.replace(/^www\./i, '').toLowerCase(); } catch (e) { return ''; } };
const norm = (v) => String(v || '').trim().toLowerCase();
// Test shops (SoftTouch's own): the only targets a shop on ANOTHER hosting
// account may be cloned into, as a debug copy (mini-api routes/clone.php).
const isTestShopHost = (h) => /(^|\.)fashionretail\.(be|eu)$/.test(String(h || ''));
// Refresh the source token this long before it runs out (auth gives 5 min).
const TOKEN_MARGIN_MS = 60000;
const RUNNING = ['queued', 'running'];
// Pause between two steps; every step itself works up to ~15 s on the shop.
const STEP_GAP_MS = 800;
// After a failed step (proxy timeout, 503 from a busy host) wait longer each
// time: 4, 8, 16, then 30 s; a step that succeeds resets it.
const RETRY_GAP_MS = 4000;
const RETRY_GAP_MAX_MS = 30000;
// The clone's phases (ShopCloner job.phase), shown as a stepper; a run
// reports the phases of its own choice (status.phases).
const PHASES = ['database', 'files', 'dirs', 'urls'];

// What a clone can take (mini-api ShopCloner::PARTS). Default: settings +
// content — the fast, common case; customers never by default (personal data).
const PARTS = ['settings', 'content', 'catalog', 'photos', 'customers'];
const DEFAULT_PARTS = ['settings', 'content'];
const PRESETS = [
    { key: 'settings', parts: ['settings'], labelKey: 'cfg_act_clone_preset_settings', label: 'Settings only' },
    { key: 'site', parts: ['settings', 'content'], labelKey: 'cfg_act_clone_preset_site', label: 'Settings + content' },
    { key: 'all', parts: PARTS, labelKey: 'cfg_act_clone_preset_all', label: 'Everything' },
];
const sameParts = (a, b) => a.length === b.length && a.every(x => b.includes(x));
// "12 min 05 s"
const fmtElapsed = (ms) => {
    const sec = Math.max(0, Math.round(ms / 1000));
    const m = Math.floor(sec / 60);
    return m ? `${m} min ${String(sec % 60).padStart(2, '0')} s` : `${sec} s`;
};
// A run the user stopped ends as "failed" with a last step "Gestopt …" (the
// mini-api has no separate state for it): shown as "stopped", not as an error.
const isStopped = (st) => !!st && st.state === 'failed'
    && /^gestopt\b/i.test(String(((st.steps || [])[(st.steps || []).length - 1] || {}).msg || ''));

/**
 * Clone another webshop of the same client into the selected one (SoftTouch
 * users only — the Actions screen shows the card when sft_status is not "0").
 * The work runs
 * on the shop server (mini-api /admin/clone/*, lib/Clone/ShopCloner):
 * database, content files, then the URLs — in short steps, because the host
 * kills long PHP runs: while this dialog is open it calls
 * /admin/clone/continue one step after the other; closed, the clone waits and
 * goes on when the dialog opens again. Between shops on the same hosting
 * account (checked here on the FTP host + user from Connecties and again by
 * the mini-api on disk), the target reads the source directly. Passwords are
 * never sent along.
 *
 * Another hosting account (2026-10-15): only into a test shop
 * (*.fashionretail.be / .eu) set up as a debug copy (config/sandbox.php on
 * that shop: mails to SoftTouch, no payments, no POS). The target reads the
 * source through the source's mini-api export routes, with a short token
 * auth issues per step (webshop&action=cloneSourceToken) — this dialog asks a
 * fresh one before it runs out and passes it along with every step.
 */
const CloneWebshopDialog = ({ open, onClose, t, api, login, target, webshops = [], notify }) => {
    const tr = (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);
    const targetBase = shopBaseUrl(target);
    const targetHost = hostOf(targetBase);
    const testTarget = isTestShopHost(targetHost);
    // Into a test shop any client's webshop can be the source (auth
    // cloneSources, SoftTouch users only); otherwise this client's own shops.
    const [allShops, setAllShops] = useState(null);
    const ownIds = new Set(webshops.map(w => String(w?.id)));
    const sources = [...webshops, ...((testTarget && allShops) || []).filter(w => !ownIds.has(String(w.id)))]
        .filter(w => w && target && w.id !== target.id && shopBaseUrl(w))
        // this client's shops first: the list is grouped (Autocomplete groupBy needs the groups together)
        .sort((a, b) => (ownIds.has(String(b.id)) - ownIds.has(String(a.id))) || shopBaseUrl(a).localeCompare(shopBaseUrl(b)));

    const [sourceId, setSourceId] = useState('');
    const [sourceQuery, setSourceQuery] = useState('');
    const [ftp, setFtp] = useState({});          // webshop id -> ftp blob | null
    const [ftpError, setFtpError] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [parts, setParts] = useState(DEFAULT_PARTS);
    // the last run's details: folded on open, unfolded while a clone runs
    const [statusOpen, setStatusOpen] = useState(false);
    const [failedOpen, setFailedOpen] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [status, setStatusState] = useState(null);
    // The step loop reads the job from statusRef. Updated together with the
    // state, not in an effect: the first step runs right after "start", before
    // React re-rendered, and a remote clone then went without its source token.
    const statusRef = useRef(null);
    const setStatus = useCallback((s) => { statusRef.current = s; setStatusState(s); }, []);
    const [starting, setStarting] = useState(false);
    const [stopping, setStopping] = useState(false);
    const loopRef = useRef({ on: false, timer: null, fails: 0 });
    // token for the source's export routes (remote clones), kept until shortly before it expires
    const tokenRef = useRef({ sourceId: null, data: null, until: 0 });

    const proxy = useCallback((method, path, body) => SftGlobalData.post('webshop&action=proxy', {
        key: api, webshop_id: target?.id, sftUserId: login?.sft_user_id, user: login?.login, method, path, ...(body ? { body } : {}),
    }), [api, target?.id, login?.sft_user_id, login?.login]);

    const loadFtp = useCallback(async (id) => {
        if (!id || ftp[id] !== undefined) return;
        const r = await SftGlobalData.post('webshop&action=getCredentials', { key: api, webshop_id: id }).catch(() => null);
        if (!r || !r.success) setFtpError(r?.message || tr('cfg_act_clone_ftp_load_failed', 'Could not load the FTP details.'));
        setFtp(prev => ({ ...prev, [id]: (r && r.success && r.data && r.data.ftp) || null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, ftp]);

    // A token for the source's mini-api export routes (remote clone), from auth.
    const sourceToken = useCallback(async (sourceWebshopId) => {
        const c = tokenRef.current;
        if (c.data && String(c.sourceId) === String(sourceWebshopId) && Date.now() < c.until - TOKEN_MARGIN_MS) return c.data;
        const r = await SftGlobalData.post('webshop&action=cloneSourceToken', {
            key: api, webshop_id: target?.id, source_webshop_id: sourceWebshopId,
            sft_user_id: login?.sft_user_id, user: login?.login,
        }).catch(() => null);
        if (!r || !r.success || !r.data?.token) {
            throw new Error(webshopErrorText(r, tr) || tr('cfg_act_clone_token_failed', 'Could not get access to the source webshop.'));
        }
        tokenRef.current = { sourceId: sourceWebshopId, data: r.data, until: Date.now() + (Number(r.data.expires_in) || 300) * 1000 };
        return r.data;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, target?.id, login?.sft_user_id, login?.login]);

    const fetchStatus = useCallback(async () => {
        const r = await proxy('GET', '/admin/clone/status').catch(() => null);
        if (!r || !r.success) return null;
        setStatus(r.data);
        return r.data;
    }, [proxy, setStatus]);
    const stopPolling = () => {
        loopRef.current.on = false;
        if (loopRef.current.timer) { clearTimeout(loopRef.current.timer); loopRef.current.timer = null; }
    };
    // Drive the clone: one step after the other until it is done/failed or
    // the dialog closes. A failed call (proxy timeout, network) is followed
    // by a status read and another try a bit later.
    const startPolling = useCallback(() => {
        stopPolling();
        loopRef.current.on = true;
        loopRef.current.fails = 0;
        const step = async () => {
            if (!loopRef.current.on) return;
            // a remote clone takes a fresh source token along with every step
            let body;
            const cur = statusRef.current;
            if (cur?.mode === 'remote' && cur.source_webshop_id) {
                const tok = await sourceToken(cur.source_webshop_id).catch(() => null);
                body = tok ? { source_token: tok.token } : undefined;
            }
            const r = await proxy('POST', '/admin/clone/continue', body).catch(() => null);
            if (!loopRef.current.on) return;
            const s = (r && r.success) ? r.data : await fetchStatus();
            if (r && r.success) setStatus(s);
            if (s && !RUNNING.includes(s.state)) {
                stopPolling();
                if (notify) notify(s.state === 'done'
                    ? tr('cfg_act_clone_done', 'Clone finished.')
                    : tr('cfg_act_clone_failed', 'Clone failed — see the log.'), s.state === 'done' ? 2 : 1);
                return;
            }
            // a source that answers 429/5xx (source_busy) backs off like a failed
            // call: hammering it every few seconds only keeps the rate limit going
            const okStep = !!(r && r.success) && !r.data?.source_busy;
            loopRef.current.fails = okStep ? 0 : loopRef.current.fails + 1;
            const gap = okStep
                ? (r.data?.busy ? RETRY_GAP_MS : STEP_GAP_MS)
                : Math.min(RETRY_GAP_MS * 2 ** (loopRef.current.fails - 1), RETRY_GAP_MAX_MS);
            loopRef.current.timer = setTimeout(step, gap);
        };
        step();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proxy, fetchStatus, notify, sourceToken]);

    useEffect(() => {
        if (!open || !target) return undefined;
        setConfirmText('');
        setParts(DEFAULT_PARTS);   // every time the safe, fast default (no customers, no photos)
        setStatusOpen(false);
        setFtpError('');
        loadFtp(target.id);
        fetchStatus().then(s => { if (s && RUNNING.includes(s.state)) startPolling(); });
        return stopPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, target?.id]);
    // another client's shop: its FTP details are not ours to read (and not needed: remote clone)
    const isForeign = (id) => !!id && !ownIds.has(String(id));
    useEffect(() => { if (open && sourceId && !isForeign(sourceId)) loadFtp(sourceId); }, [open, sourceId, loadFtp]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!open || !target || !testTarget || allShops !== null) return;
        SftGlobalData.post('webshop&action=cloneSources', {
            key: api, webshop_id: target.id, sft_user_id: login?.sft_user_id, user: login?.login,
        }).then(r => setAllShops(r && r.success && Array.isArray(r.data) ? r.data : []))
            .catch(() => setAllShops([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, target?.id, testTarget]);

    const source = sources.find(w => String(w.id) === String(sourceId)) || null;
    const tf = target ? ftp[target.id] : undefined;
    const sf = source ? (isForeign(source.id) ? null : ftp[source.id]) : undefined;
    const ftpReady = tf !== undefined && (!source || sf !== undefined);
    let accountCheck = null;   // null = not decidable yet; mode 'local' | 'remote'
    if (source && ftpReady) {
        const sameAccount = !!(tf && sf && tf.host && sf.host && norm(tf.host) === norm(sf.host) && norm(tf.username) === norm(sf.username));
        if (sameAccount) accountCheck = { ok: true, mode: 'local', msg: tr('cfg_act_clone_same_account', 'Same hosting account — the clone can run on the server.') };
        else if (testTarget) accountCheck = { ok: true, mode: 'remote', msg: tr('cfg_act_clone_remote_ok', 'Another hosting account: %host% reads the source through its mini-api and becomes a debug copy — e-mails go to SoftTouch, payments and the POS are switched off. The source needs a mini-api with the export routes.').replace('%host%', targetHost) };
        else if (!tf || !sf || !tf.host || !sf.host) accountCheck = { ok: false, msg: tr('cfg_act_clone_ftp_missing', 'FTP details are missing in Connections for one of both webshops.') };
        else accountCheck = { ok: false, msg: tr('cfg_act_clone_other_account', 'These webshops are not on the same hosting account (different FTP host or user). Cloning only works within one account.') };
    }
    const running = !!status && RUNNING.includes(status.state);
    const confirmed = !!targetHost && norm(confirmText).replace(/^www\./, '') === targetHost;
    const canStart = !!source && !!accountCheck?.ok && confirmed && !running && !starting && parts.length > 0;

    const start = async () => {
        if (!canStart) return;
        setStarting(true);
        try {
            let body;
            if (accountCheck.mode === 'remote') {
                const tok = await sourceToken(source.id);
                body = {
                    confirm:      true,
                    mode:         'remote',
                    source_api:   tok.source_api,
                    source_token: tok.token,
                    source_url:   tok.source_url || shopBaseUrl(source),
                    source_webshop_id: source.id,
                    parts:        PARTS.filter(x => parts.includes(x)),
                    target_url:   targetBase,
                };
            } else {
                body = {
                    confirm:    true,
                    source_dir: sf.directory || '',
                    target_dir: tf.directory || '',
                    source_url: shopBaseUrl(source),
                    parts:      PARTS.filter(x => parts.includes(x)),
                    target_url: targetBase,
                };
            }
            const r = await proxy('POST', '/admin/clone/start', body);
            if (r && r.success) {
                setConfirmText('');
                await fetchStatus();
                startPolling();
                return;
            }
            const s = await fetchStatus();
            if (s && RUNNING.includes(s.state)) {
                setConfirmText('');
                startPolling();
            } else if (notify) {
                notify(`${tr('cfg_act_clone_title', 'Clone webshop')}: ${webshopErrorText(r, tr) || tr('cfg_act_failed', 'failed')}`, 1);
            }
        } catch (e) {
            const s = await fetchStatus();
            if (s && RUNNING.includes(s.state)) startPolling();
            else if (notify) notify(e?.message || tr('cfg_act_failed', 'failed'), 1);
        } finally {
            setStarting(false);
        }
    };

    const stop = async () => {
        setStopping(true);
        stopPolling();
        const r = await proxy('POST', '/admin/clone/cancel').catch(() => null);
        if (r && r.success) setStatus(r.data);
        const s = await fetchStatus();
        // a step that was running finishes its current file first
        if (s && RUNNING.includes(s.state)) startPolling();
        setStopping(false);
    };

    // copy only the files the last clone listed as failed (mini-api
    // /admin/clone/retry-files); the steps run through the usual loop, which
    // also brings a remote source its token
    const retryFailed = async () => {
        setRetrying(true);
        try {
            const r = await proxy('POST', '/admin/clone/retry-files').catch(() => null);
            if (r && r.success) {
                setStatus(r.data);
                startPolling();
            } else if (notify) {
                notify(webshopErrorText(r, tr) || tr('cfg_act_failed', 'failed'), 1);
            }
        } finally {
            setRetrying(false);
        }
    };

    // elapsed time ticks every second while a run is going
    const [, setNow] = useState(0);
    useEffect(() => {
        if (!running) return undefined;
        const id = setInterval(() => setNow(x => x + 1), 1000);
        return () => clearInterval(id);
    }, [running]);

    useEffect(() => { if (running) setStatusOpen(true); }, [running]);

    const stopped = isStopped(status);
    const shownState = stopped ? 'stopped' : status?.state;
    const stateChip = status && status.state !== 'idle' && (
        <Chip size="small"
            color={shownState === 'done' ? 'success' : shownState === 'failed' ? 'error' : shownState === 'stopped' ? 'warning' : 'info'}
            label={tr(`cfg_act_clone_state_${shownState}`, shownState)} />
    );
    const startedAt = status?.started_at ? new Date(status.started_at).getTime() : null;
    const endedAt = status?.finished_at ? new Date(status.finished_at).getTime() : null;
    const elapsed = startedAt ? fmtElapsed((running ? Date.now() : (endedAt || Date.now())) - startedAt) : '';
    const runPhases = Array.isArray(status?.phases) && status.phases.length ? status.phases : PHASES;
    // a phase the run does not show (e.g. the quick file step of a photos-only
    // clone) counts as the next one it does show
    const phaseIdx = status?.state === 'done' ? runPhases.length : (() => {
        const order = PHASES.indexOf(status?.phase || 'database');
        const i = runPhases.findIndex(ph => PHASES.indexOf(ph) >= order);
        return i === -1 ? runPhases.length - 1 : i;
    })();
    // the database step reports every 10 tables; only the last of those lines
    // is worth showing (while it is still the newest line)
    const steps = (status?.steps || []).filter((s, i, all) => !(s.ok && s.step === 'database' && /…$/.test(String(s.msg)) && i < all.length - 1));

    // Direction: what is copied onto what (the most important thing to get right)
    const dirSource = running || (status && status.state !== 'idle' && !source) ? (status?.source_url || '') : (source ? shopBaseUrl(source) : '');
    const dirTarget = (running && status?.target_url) ? status.target_url : targetBase;
    const hostLabel = (u) => hostOf(u) || u;
    const direction = (
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, flexWrap: 'wrap' }} data-clone-direction>
            <Box sx={{ flex: '1 1 220px', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>{tr('cfg_act_clone_dir_source', 'Source')}</Typography>
                {running ? (
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>{hostLabel(dirSource) || '?'}</Typography>
                ) : (
                    // the source is chosen right here, in its own box: this client's
                    // shops right away; another client's shop (test-shop target) by typing
                    <Autocomplete
                        fullWidth
                        options={sources}
                        value={source}
                        onChange={(_e, w) => setSourceId(w ? String(w.id) : '')}
                        getOptionLabel={(w) => shopBaseUrl(w)}
                        isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                        groupBy={(w) => (isForeign(w.id) ? tr('cfg_act_clone_group_other', 'Other webshops') : tr('cfg_act_clone_group_client', 'This client'))}
                        filterOptions={(opts, { inputValue }) => {
                            const q = norm(inputValue);
                            // nothing typed: only this client's shops; typing searches every shop
                            return q === '' ? opts.filter(w => !isForeign(w.id))
                                : opts.filter(w => norm(shopBaseUrl(w)).includes(q) || norm(w.slug).includes(q));
                        }}
                        noOptionsText={norm(sourceQuery) === '' && testTarget
                            ? tr('cfg_act_clone_type_to_search', 'Type to search the webshops of other clients')
                            : tr('cfg_act_clone_no_match', 'No webshop found')}
                        loading={testTarget && allShops === null}
                        loadingText={tr('cfg_act_clone_loading_sources', 'Loading webshops…')}
                        inputValue={sourceQuery}
                        onInputChange={(_e, v) => setSourceQuery(v)}
                        renderInput={(params) => (
                            <TextField {...params} variant="standard"
                                placeholder={tr('cfg_act_clone_dir_pick', 'Choose a source')}
                                helperText={sources.length ? (testTarget ? tr('cfg_act_clone_search_hint', 'Type to search other clients too') : '') : tr('cfg_act_clone_no_sources', 'This client has no other webshop.')}
                                inputProps={{ ...params.inputProps, 'aria-label': tr('cfg_act_clone_source', 'Source webshop'), 'data-clone-source': true }}
                                InputProps={{ ...params.InputProps, disableUnderline: !!source, sx: { fontWeight: 700, fontSize: '1rem' } }} />
                        )}
                        sx={{ my: 0.25 }}
                    />
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_act_clone_dir_source_note', 'is copied, stays unchanged')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}><ArrowForwardIcon sx={{ fontSize: 32, color: 'text.secondary' }} /></Box>
            <Box sx={{ flex: '1 1 220px', border: '2px solid', borderColor: 'error.main', borderRadius: 1.5, px: 1.5, py: 1, backgroundColor: 'rgba(211,47,47,0.04)' }}>
                <Typography variant="caption" sx={{ color: 'error.main', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{tr('cfg_act_clone_dir_target', 'Target')}</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>{hostLabel(dirTarget) || '?'}</Typography>
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>{tr('cfg_act_clone_dir_overwritten', 'is overwritten')}</Typography>
            </Box>
        </Box>
    );

    const listSx = { m: 0, pl: 2.25, '& li': { fontSize: '0.84rem', lineHeight: 1.5 } };
    // what each part replaces on the target, in the warning
    const REPLACES = {
        settings: [tr('cfg_act_clone_rep_settings', 'Settings, translations, countries and stores'), tr('cfg_act_clone_rep_library', 'Logo, header and uploaded images (library)'), tr('cfg_act_clone_rep_files', 'cookie.js, fonts and scripts')],
        content: [tr('cfg_act_clone_rep_pages', 'Pages, containers, banners, carousel and image grid'), tr('cfg_act_clone_rep_vouchers', 'Voucher layouts and translations, promotions, looks')],
        catalog: [tr('cfg_act_clone_rep_catalog', 'Products, sizes, stock, brands, categories, colours, seasons')],
        photos: [tr('cfg_act_clone_rep_photos', 'Product photos')],
        customers: [tr('cfg_act_clone_rep_customers', 'Customers, addresses, carts, reservations and vouchers')],
    };
    const chosen = PARTS.filter(x => parts.includes(x));
    const notChosen = PARTS.filter(x => !parts.includes(x));
    const withUrls = chosen.some(x => x !== 'photos');
    const warning = (
        <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }} data-clone-warning>
            <AlertTitle sx={{ fontWeight: 700, mb: 0.75 }}>{tr('cfg_act_clone_warn_title_parts', 'On %host% this is replaced by the source:').replace('%host%', targetHost || '?')}</AlertTitle>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{tr('cfg_act_clone_replaced', 'Replaced')}</Typography>
                    <Box component="ul" sx={listSx}>
                        {chosen.flatMap(x => REPLACES[x]).map(t => <li key={t}>{t}</li>)}
                        {chosen.length === PARTS.length && <li>{tr('cfg_act_clone_rep_other', 'All other tables of the shop')}</li>}
                    </Box>
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{tr('cfg_act_clone_kept', 'Kept')}</Typography>
                    <Box component="ul" sx={listSx}>
                        {notChosen.map(x => <li key={x}>{tr(`cfg_act_clone_part_${x}`, x)}</li>)}
                        <li>{tr('cfg_act_clone_keep_client', 'The client (index.php)')}</li>
                        <li>config.php</li>
                        <li>{tr('cfg_act_clone_keep_web', 'robots.txt and .htaccess')}</li>
                        <li>{tr('cfg_act_clone_keep_logs', 'The admin audit and the logs of this shop')}</li>
                    </Box>
                </Box>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
                {withUrls ? tr('cfg_act_clone_warn_urls', 'Then the URLs are switched to this shop.') + ' ' : ''}
                {tr('cfg_act_clone_warn_edit_source', 'Edit on the source: changes made here are gone with the next clone.')}
            </Typography>
        </Alert>
    );

    // what to take along: presets + one checkbox per part
    const PART_INFO = {
        settings:  { desc: tr('cfg_act_clone_part_settings_desc', 'All settings, translations, countries and stores, logo and uploaded images, cookie.js, fonts and scripts.'), size: tr('cfg_act_clone_size_seconds', 'seconds') },
        content:   { desc: tr('cfg_act_clone_part_content_desc', 'Pages, product containers, banners, carousel, image grid, links, looks, promotions and voucher layouts, with their images.'), size: tr('cfg_act_clone_size_minute', 'up to a minute') },
        catalog:   { desc: tr('cfg_act_clone_part_catalog_desc', 'Products, sizes, stock, brands, categories, colours, seasons and size tables. Normally the POS sync fills these.'), size: tr('cfg_act_clone_size_seconds', 'seconds') },
        photos:    { desc: tr('cfg_act_clone_part_photos_desc', 'All product photos: by far the slowest part (tens of thousands of files). Alternative: "Force image sync" on this shop.'), size: tr('cfg_act_clone_size_long', 'minutes to half an hour') },
        customers: { desc: tr('cfg_act_clone_part_customers_desc', 'Customers, addresses, carts, reservations and vouchers. Personal data: only when really needed.'), size: tr('cfg_act_clone_size_seconds', 'seconds'), sensitive: true },
    };
    const togglePart = (x) => setParts(prev => (prev.includes(x) ? prev.filter(p => p !== x) : [...prev, x]));
    const partPicker = (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }} data-clone-parts>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 'auto' }}>{tr('cfg_act_clone_parts_title', 'What do you take over?')}</Typography>
                {PRESETS.map(pr => {
                    const on = sameParts(parts, pr.parts);
                    return (
                        <Button key={pr.key} size="small" variant={on ? 'contained' : 'outlined'} disableElevation
                            onClick={() => setParts(pr.parts)} sx={{ textTransform: 'none', py: 0.25 }} data-clone-preset={pr.key}>
                            {tr(pr.labelKey, pr.label)}
                        </Button>
                    );
                })}
            </Box>
            {PARTS.map(x => (
                <Box key={x} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.25 }}>
                    <Checkbox size="small" checked={parts.includes(x)} onChange={() => togglePart(x)} sx={{ p: 0.5 }}
                        inputProps={{ 'aria-label': tr(`cfg_act_clone_part_${x}`, x), 'data-clone-part': x }} />
                    <Box sx={{ flex: 1, minWidth: 0, pt: 0.4, cursor: 'pointer' }} onClick={() => togglePart(x)}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{tr(`cfg_act_clone_part_${x}`, x)}</Typography>
                            <Typography variant="caption" sx={{ color: x === 'photos' ? 'warning.main' : 'text.secondary', fontWeight: x === 'photos' ? 600 : 400 }}>{PART_INFO[x].size}</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', color: PART_INFO[x].sensitive ? 'error.main' : 'text.secondary', lineHeight: 1.4 }}>{PART_INFO[x].desc}</Typography>
                    </Box>
                </Box>
            ))}
            {parts.length === 0 && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{tr('cfg_act_clone_parts_none', 'Choose at least one part.')}</Typography>
            )}
            {parts.includes('content') && !parts.includes('catalog') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }} data-clone-content-note>
                    {tr('cfg_act_clone_content_note', 'Containers and filters show the products of this shop; when they differ from the source, a container can be empty.')}
                </Typography>
            )}
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }} data-clone-dialog>
            <DialogTitle>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    {tr('cfg_act_clone_dialog_title', 'Clone a webshop into %host%').replace('%host%', targetHost || '?')}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {direction}
                    {/* the last run first: its result is what you look for after a clone */}
                    {status && status.state !== 'idle' && (
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }} data-clone-status>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}
                                role="button" tabIndex={0} aria-expanded={statusOpen} data-clone-status-toggle
                                onClick={() => setStatusOpen(o => !o)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStatusOpen(o => !o); } }}>
                                <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary', transition: 'transform 150ms', transform: statusOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{running ? tr('cfg_act_clone_running_title', 'Clone in progress') : tr('cfg_act_clone_last_run', 'Last clone')}</Typography>
                                {stateChip}
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {[!running && status.source_url && `${hostLabel(status.source_url)} → ${hostLabel(status.target_url)}`,
                                        Array.isArray(status.parts) && status.parts.length < PARTS.length && status.parts.map(x => tr(`cfg_act_clone_part_${x}`, x)).join(', '),
                                        status.started_by, status.started_at && new Date(status.started_at).toLocaleString(),
                                        elapsed && (running ? tr('cfg_act_clone_elapsed_running', 'running for %time%') : tr('cfg_act_clone_elapsed_done', 'took %time%')).replace('%time%', elapsed)].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                            <Collapse in={statusOpen} unmountOnExit>
                            <Box sx={{ pt: 1 }} data-clone-status-body>
                            {status.state === 'done' && (
                                <Alert severity="success" sx={{ mb: 1.5 }} data-clone-done
                                    action={status.target_url ? (
                                        <Button color="inherit" size="small" endIcon={<OpenInNewIcon fontSize="small" />} href={status.target_url} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                                            {tr('cfg_act_clone_open_target', 'Open %host%').replace('%host%', hostLabel(status.target_url))}
                                        </Button>
                                    ) : undefined}>
                                    <AlertTitle sx={{ mb: 0 }}>{tr('cfg_act_clone_done_title', 'Clone succeeded')}</AlertTitle>
                                    {tr('cfg_act_clone_done_body', '%target% is now a copy of %source%.').replace('%target%', hostLabel(status.target_url)).replace('%source%', hostLabel(status.source_url))}
                                </Alert>
                            )}
                            {status.state === 'done' && Number(status.failed_count) > 0 && (
                                <Alert severity="warning" sx={{ mb: 1.5 }} data-clone-failed-files
                                    action={(
                                        <Button color="inherit" size="small" onClick={retryFailed} disabled={retrying} data-clone-retry-files
                                            startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : null} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                                            {tr('cfg_act_clone_retry_files', 'Try failed files again')}
                                        </Button>
                                    )}>
                                    <AlertTitle sx={{ mb: 0.25 }}>
                                        {tr('cfg_act_clone_failed_files_title', '%n% file(s) not copied').replace('%n%', String(status.failed_count))}
                                    </AlertTitle>
                                    {tr('cfg_act_clone_failed_files_body', 'The rest of the clone went on. These files can be tried again on their own.')}
                                    <Box component="span" role="button" tabIndex={0} onClick={() => setFailedOpen(o => !o)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFailedOpen(o => !o); } }}
                                        sx={{ display: 'block', mt: 0.5, cursor: 'pointer', textDecoration: 'underline', fontSize: '0.82rem' }} data-clone-failed-toggle>
                                        {failedOpen ? tr('cfg_act_clone_failed_files_hide', 'Hide the list') : tr('cfg_act_clone_failed_files_show', 'Show the list')}
                                    </Box>
                                    <Collapse in={failedOpen} unmountOnExit>
                                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: 220, overflowY: 'auto' }} data-clone-failed-list>
                                            {(status.failed_files || []).map(f => (
                                                <li key={f.path} style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                                    <b>{f.path}</b>{f.error ? ` — ${f.error}` : ''}
                                                </li>
                                            ))}
                                        </Box>
                                        {Number(status.failed_count) > (status.failed_files || []).length && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                {tr('cfg_act_clone_failed_files_more', 'and %n% more').replace('%n%', String(Number(status.failed_count) - (status.failed_files || []).length))}
                                            </Typography>
                                        )}
                                    </Collapse>
                                </Alert>
                            )}
                            {stopped && (
                                <Alert severity="warning" icon={<PauseCircleOutlineIcon />} sx={{ mb: 1.5 }} data-clone-stopped>
                                    {tr('cfg_act_clone_stopped_body', 'The clone was stopped. What was already copied stays; start it again to finish.')}
                                </Alert>
                            )}
                            {(running || status.state === 'done') && status.phase !== 'retry' && (
                                <Stepper activeStep={phaseIdx} alternativeLabel sx={{ mb: 1.5 }} data-clone-stepper>
                                    {runPhases.map(ph => (
                                        <Step key={ph}><StepLabel>{tr(`cfg_act_clone_phase_${ph}`, ph)}</StepLabel></Step>
                                    ))}
                                </Stepper>
                            )}
                            {running && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1, mb: 1, borderRadius: 1, backgroundColor: 'action.hover' }} data-clone-progress>
                                    <Box sx={{ width: 18, height: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CircularProgress size={16} thickness={5} />
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                        {status.progress || tr(`cfg_act_clone_phase_${runPhases[phaseIdx] || 'database'}`, '') + '…'}
                                        {Number(status.failed_count) > 0 && (
                                            <Box component="span" sx={{ fontWeight: 400, color: 'warning.main', ml: 1 }} data-clone-failed-running>
                                                {tr('cfg_act_clone_failed_files_so_far', '(%n% file(s) failed so far)').replace('%n%', String(status.failed_count))}
                                            </Box>
                                        )}
                                    </Typography>
                                </Box>
                            )}
                            {steps.map((s, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', py: 0.25 }}>
                                    {s.ok ? <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16, mt: '2px' }} />
                                        : (stopped && i === steps.length - 1 ? <PauseCircleOutlineIcon color="warning" sx={{ fontSize: 16, mt: '2px' }} /> : <ErrorOutlineIcon color="error" sx={{ fontSize: 16, mt: '2px' }} />)}
                                    <Typography variant="body2" sx={{ fontSize: '0.82rem', wordBreak: 'break-word' }}>{s.msg}</Typography>
                                </Box>
                            ))}
                            {running && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }} data-clone-keep-open>
                                    {tr('cfg_act_clone_keep_open', 'The clone runs in short steps while this window is open. Closed, it pauses and continues when you open it again.')}
                                </Typography>
                            )}
                            </Box>
                            </Collapse>
                        </Box>
                    )}
                    {/* setup: hidden while a clone runs (nothing to choose then) */}
                    {!running && partPicker}
                    {!running && parts.length > 0 && warning}
                    {!running && source && !ftpReady && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={16} /><Typography variant="body2">{tr('cfg_act_clone_checking', 'Checking the hosting account…')}</Typography></Box>}
                    {!running && ftpError && <Alert severity="error">{ftpError}</Alert>}
                    {!running && accountCheck && <Alert severity={accountCheck.ok ? 'success' : 'error'}>{accountCheck.msg}</Alert>}
                    {!running && source && accountCheck?.ok && (
                        <TextField size="small" fullWidth value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                            label={tr('cfg_act_clone_confirm_label', 'Type %host% to confirm').replace('%host%', targetHost)}
                            inputProps={{ 'data-clone-confirm': true, autoComplete: 'off' }} />
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                {running && (
                    <Button color="error" onClick={stop} disabled={stopping} sx={{ textTransform: 'none', mr: 'auto' }}
                        startIcon={stopping ? <CircularProgress size={14} color="inherit" /> : null}>
                        {tr('cfg_act_clone_cancel', 'Stop clone')}
                    </Button>
                )}
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>{tr('cfg_act_close', 'Close')}</Button>
                {!running && (
                    <Button variant="contained" color="error" onClick={start} disabled={!canStart}
                        startIcon={starting ? <CircularProgress size={16} color="inherit" /> : null} sx={{ textTransform: 'none' }}>
                        {tr('cfg_act_clone_start', 'Start clone')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

CloneWebshopDialog.propTypes = {
    open: PropTypes.bool, onClose: PropTypes.func, t: PropTypes.func, api: PropTypes.string, login: PropTypes.object,
    target: PropTypes.object, webshops: PropTypes.array, notify: PropTypes.func,
};

export default CloneWebshopDialog;
